import { useMemo } from "react";

import useAuth from "$src/core/hooks/use-auth";
import useConfig from "$src/core/hooks/use-config";
import useTranslation from "$src/core/hooks/use-translation";
import useAboutQuery from "$src/core/queries/use-about-query";

export type ProfileMenuItem = {
  id: string;
  icon: "document" | "tv" | "search" | "circle-info";
  title: string;
  content?: string;
};

export type ProfileMenuGroup = {
  title: string;
  items: ProfileMenuItem[];
};

const useProfileMenu = (): ProfileMenuGroup[] => {
  const { t } = useTranslation();
  const { name } = useConfig();
  const { status: statusAuth } = useAuth();

  const { data: about, status: statusAbout } = useAboutQuery();

  return useMemo(
    () =>
      statusAbout !== "pending"
        ? [
            {
              title: name,
              items: [
                ...(about?.items ?? []).map((item, index) => ({
                  ...item,
                  id: `${item.title ?? ""}-${index}`,
                  title: item.title ?? "",
                  icon: "document" as const,
                })),
              ],
            },
            ...(statusAuth === "authenticated"
              ? [
                  {
                    title: t("PROFILE/GROUP/ACCOUNT"),
                    items: [
                      {
                        id: "subscription-offers",
                        title: t("PROFILE_PAGE/NAV_SUBSCRIPTION_OFFERS_LABEL"),
                        icon: "tv" as const,
                        content: undefined,
                      },
                    ],
                  },
                ]
              : []),
            {
              title: t("PROFILE/GROUP/SETTINGS"),
              items: [
                {
                  id: "search",
                  title: t("SEARCH_SETTINGS_PANEL/TITLE"),
                  icon: "search" as const,
                  content: undefined,
                },
                {
                  id: "debug",
                  title: t("DEBUG/TITLE"),
                  icon: "circle-info" as const,
                  content: undefined,
                },
              ],
            },
          ].filter(Boolean)
        : [],
    [statusAbout, name, about?.items, t, statusAuth],
  );
};

export default useProfileMenu;
